/**
 * TopCreativesTable
 * ─────────────────
 * Closed-loop attribution table per ad creative.
 *
 * Pulls `/api/lead-analytics/by-creative` and shows leads / won / revenue /
 * win rate per (platform, campaign, ad). The biggest argument for AEGIS over
 * generic dashboards: agencies can see which CREATIVE — not just which
 * campaign — generates real revenue, and pause the dead ones.
 *
 * Empty state shown until the agency configures the Meta/LinkedIn webhook
 * with platform metadata fields.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_LABEL = {
  meta: { name: 'Meta', color: 'bg-blue-100 text-blue-700' },
  linkedin: { name: 'LinkedIn', color: 'bg-sky-100 text-sky-700' },
  google_form: { name: 'Google', color: 'bg-amber-100 text-amber-700' },
};

const fmtMoney = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} €`;
const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

export default function TopCreativesTable({ clientId, period = '90d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const days = parseInt(period.replace('d', ''), 10) || 90;
      const end = new Date();
      const start = new Date(end.getTime() - days * 86400 * 1000);
      const params = new URLSearchParams({
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
      });
      if (clientId && clientId !== 'all') params.set('client_id', clientId);
      if (platformFilter !== 'all') params.set('platform', platformFilter);
      const { data: res } = await axios.get(`${API}/lead-analytics/by-creative?${params}`, { withCredentials: true });
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [clientId, period, platformFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="border rounded-lg bg-white p-12 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const byPlatform = data?.by_platform || {};

  // Empty state — no leads with platform metadata yet
  if (!rows.length && !error) {
    return (
      <div className="border border-dashed border-violet-300 bg-violet-50/40 rounded-lg p-8 text-center">
        <Sparkles className="w-8 h-8 text-violet-500 mx-auto mb-3" />
        <h3 className="font-medium text-neutral-900 mb-1">Aucun lead Meta / LinkedIn pour l'instant</h3>
        <p className="text-sm text-neutral-600 max-w-xl mx-auto">
          Pour voir le ROI par creative, configurez le <strong>webhook leads</strong> dans Zapier (ou directement Meta/LinkedIn) en y ajoutant les champs <code className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded">campaign_name</code>, <code className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded">ad_name</code>, <code className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded">ad_id</code>. Les exemples détaillés sont dans la documentation du dialog Webhook leads.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="top-creatives-table">
      <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-violet-50/60 to-transparent">
        <div>
          <h3 className="font-medium text-neutral-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" /> Top creatives — closed-loop ROI
          </h3>
          <p className="text-[12px] text-neutral-500 mt-0.5">
            {totals.creatives_count || 0} creative{totals.creatives_count > 1 ? 's' : ''} ·
            {' '}{totals.leads_total || 0} leads ·
            {' '}{totals.won_total || 0} ventes ·
            {' '}<strong className="text-emerald-700">{fmtMoney(totals.revenue_total)}</strong> de CA
          </p>
        </div>
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="w-44" data-testid="creatives-platform-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes plateformes</SelectItem>
            <SelectItem value="meta">Meta uniquement</SelectItem>
            <SelectItem value="linkedin">LinkedIn uniquement</SelectItem>
            <SelectItem value="google_form">Google Form uniquement</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Per-platform KPI strip */}
      {Object.keys(byPlatform).length > 1 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-neutral-100">
          {Object.entries(byPlatform).map(([p, agg]) => {
            const meta = PLATFORM_LABEL[p] || { name: p, color: 'bg-neutral-100 text-neutral-700' };
            const winRate = agg.leads ? agg.won / agg.leads : 0;
            return (
              <div key={p} className="bg-white p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${meta.color}`}>{meta.name}</span>
                  <span className="text-[10px] text-neutral-500">{agg.creatives} creative(s)</span>
                </div>
                <div className="text-[20px] font-semibold tabular-nums">{fmtMoney(agg.revenue)}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {agg.leads} leads · {agg.won} ventes · taux {fmtPct(winRate)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Rows */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-neutral-500 bg-neutral-50">
            <tr>
              <th className="text-left px-3 py-2">Plateforme</th>
              <th className="text-left px-3 py-2">Campagne</th>
              <th className="text-left px-3 py-2">Ad / Creative</th>
              <th className="text-right px-3 py-2">Leads</th>
              <th className="text-right px-3 py-2">Ventes</th>
              <th className="text-right px-3 py-2">Taux</th>
              <th className="text-right px-3 py-2">CA</th>
              <th className="text-right px-3 py-2">€/lead</th>
              <th className="text-center px-3 py-2 w-12">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.map((r, idx) => {
              const meta = PLATFORM_LABEL[r.platform] || { name: r.platform, color: 'bg-neutral-100 text-neutral-700' };
              const isWinner = r.win_rate >= 0.3 && r.revenue > 0;
              const isDeadDuck = r.leads_count >= 3 && r.won_count === 0;
              return (
                <tr key={idx} className="hover:bg-neutral-50/60" data-testid={`creative-row-${idx}`}>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${meta.color}`}>{meta.name}</span>
                  </td>
                  <td className="px-3 py-2 max-w-[200px] truncate" title={r.campaign_name}>{r.campaign_name}</td>
                  <td className="px-3 py-2 max-w-[220px]">
                    <div className="font-medium truncate" title={r.ad_name}>{r.ad_name}</div>
                    {r.form_name && (
                      <div className="text-[11px] text-neutral-500 truncate" title={r.form_name}>📋 {r.form_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.leads_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{r.won_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtPct(r.win_rate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtMoney(r.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmtMoney(r.revenue_per_lead)}</td>
                  <td className="px-3 py-2 text-center">
                    {isWinner && (
                      <span title="Performant — à pousser !" className="inline-flex">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                      </span>
                    )}
                    {isDeadDuck && (
                      <span title="≥ 3 leads et 0 vente — à couper" className="inline-flex">
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="p-3 border-t bg-amber-50 text-amber-800 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
    </div>
  );
}
